/**
 * BOGO Auto-Add — "buy one, second one free" without the customer
 * having to add two.
 *
 * HOW IT WORKS
 * For every BOGO product in the cart, this keeps a second, separate
 * line of the SAME variant (tagged with a hidden line property) whose
 * quantity always mirrors the customer's own quantity. A native Shopify
 * Buy X Get Y automatic discount (buy 1 / get 1 free, same products)
 * then makes the matching units free. The script never sets a price —
 * Shopify's discount does that, so checkout is always correct.
 *
 *   customer adds 1  -> cart has 1 paid + 1 tagged "free" line
 *   customer sets 3  -> 3 paid + 3 free
 *   customer removes -> free line goes too
 *
 * SETUP
 * 1. Admin: Discounts -> Create -> Buy X Get Y -> Automatic.
 *      Customer buys:  minimum quantity 1, the BOGO products/collection
 *      Customer gets:  quantity 1, the SAME products/collection, Free
 *      Uncheck "maximum uses per order" (so 3 paid units get 3 free).
 * 2. Add this file as assets/bogo-auto-add.js and include it before
 *    </body> in theme.liquid:
 *      <script src="{{ 'bogo-auto-add.js' | asset_url }}" defer></script>
 * 3. Fill in BOGO_PRODUCT_IDS below (the number at the end of the
 *    product's admin URL: /products/<this number>).
 * 4. Keep free-gift-cart.js switched off (PROMO_ENABLED = false).
 *
 * TO END THE PROMO: set PROMO_ENABLED = false and deactivate the
 * discount in admin. Free lines already sitting in someone's cart are
 * removed the next time they load the cart while this is on; with it
 * off they'd just be full price, so deactivate the discount and the
 * script on the same day.
 */

(function () {
  var PROMO_ENABLED = false;
  if (!PROMO_ENABLED) return;

  if (window.__bogoAutoAddActive) return;
  window.__bogoAutoAddActive = true;

  // Admin product IDs (NOT variant IDs) of the products in the BOGO.
  var BOGO_PRODUCT_IDS = [
    3761678663,
    3761677703,
    3761676679,
  ];

  var FREE_PROP = "_bogo_free"; // hidden line property marking the free line
  var COOLDOWN_MS = 5 * 60 * 1000; // after a failed add (e.g. out of stock), don't retry that variant for 5 min

  var isChecking = false;
  var pendingRecheck = false;
  var unavailableUntil = {};

  var RELOAD_GUARD_KEY = "bogoReloadGuardAt";
  var RELOAD_GUARD_MS = 4000;

  function isBogoProduct(item) {
    return BOGO_PRODUCT_IDS.indexOf(item.product_id) !== -1;
  }

  function isFreeLine(item) {
    return !!(item.properties && item.properties[FREE_PROP]);
  }

  async function getCart() {
    var res = await fetch("/cart.js", { headers: { Accept: "application/json" }, cache: "no-store" });
    return res.json();
  }

  async function addFree(variantId, qty) {
    var props = {};
    props[FREE_PROP] = "true";
    var res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty, properties: props }] }),
    });
    if (!res.ok) {
      var err = await res.json().catch(function () {
        return null;
      });
      console.warn("BOGO: could not add free unit for variant " + variantId + " (likely out of stock):", res.status, err);
      unavailableUntil[variantId] = Date.now() + COOLDOWN_MS;
      return false;
    }
    return true;
  }

  async function setLineQuantities(updates) {
    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: updates }),
    });
  }

  function canAutoReload() {
    var last = 0;
    try {
      last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    } catch (e) {}
    return Date.now() - last > RELOAD_GUARD_MS;
  }

  function refreshCartUI() {
    if (window.location.pathname === "/cart") {
      if (!canAutoReload()) {
        console.warn("BOGO: skipping reload, one just happened.");
        return;
      }
      try {
        sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      } catch (e) {}
      window.location.reload();
      return;
    }
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  // Brings every BOGO variant's free quantity in line with its paid
  // quantity. Returns true if it changed anything. Only acts on a
  // mismatch, so once the cart is in sync it does nothing — that's
  // what keeps it from looping.
  async function syncBogo(cart) {
    var paid = {}; // variantId -> paid qty
    var free = {}; // variantId -> { qty, keys: [] }

    cart.items.forEach(function (item) {
      if (!isBogoProduct(item)) return;
      var v = item.variant_id;
      if (isFreeLine(item)) {
        free[v] = free[v] || { qty: 0, keys: [] };
        free[v].qty += item.quantity;
        free[v].keys.push(item.key);
      } else {
        paid[v] = (paid[v] || 0) + item.quantity;
      }
    });

    var changed = false;
    var updates = {};
    var now = Date.now();

    // Paid units exist: make the free line match.
    Object.keys(paid).forEach(function (v) {
      var variantId = Number(v);
      var want = paid[v];
      var have = free[v] ? free[v].qty : 0;
      if (want === have) return;

      if (!free[v]) {
        if (unavailableUntil[variantId] && unavailableUntil[variantId] > now) return;
        updates["__add_" + v] = want; // handled below (needs /cart/add.js)
      } else {
        // First free line gets the target quantity; any extras go to 0.
        updates[free[v].keys[0]] = want;
        free[v].keys.slice(1).forEach(function (k) {
          updates[k] = 0;
        });
        changed = true;
      }
    });

    // Free lines whose paid units are gone: remove them.
    Object.keys(free).forEach(function (v) {
      if (paid[v]) return;
      free[v].keys.forEach(function (k) {
        updates[k] = 0;
      });
      changed = true;
    });

    // Do the adds first (they need /cart/add.js), then line updates.
    var addKeys = Object.keys(updates).filter(function (k) {
      return k.indexOf("__add_") === 0;
    });
    for (var i = 0; i < addKeys.length; i++) {
      var variantId = Number(addKeys[i].replace("__add_", ""));
      var ok = await addFree(variantId, updates[addKeys[i]]);
      if (ok) changed = true;
      delete updates[addKeys[i]];
    }

    if (Object.keys(updates).length) {
      await setLineQuantities(updates);
    }
    return changed;
  }

  async function check() {
    if (isChecking) {
      pendingRecheck = true;
      return;
    }
    isChecking = true;
    try {
      if (!BOGO_PRODUCT_IDS.length) return;
      var cart = await getCart();
      var changed = await syncBogo(cart);
      if (changed) refreshCartUI();
    } catch (err) {
      console.error("BOGO sync error:", err);
    } finally {
      isChecking = false;
      if (pendingRecheck) {
        pendingRecheck = false;
        check();
      }
    }
  }

  // Re-check after ANY cart change, however the theme makes it.
  // Several watchers feed one debounced check, so a burst of cart
  // requests (including our own) collapses into a single pass.
  var cartUrl = /\/cart\/(add|change|update|clear)/;
  var debounceTimer = null;

  function scheduleCheck() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(check, 150);
  }

  // 1) The browser's own network log. This sees every request to the
  //    cart endpoints even when the theme calls fetch() through a saved
  //    reference that bypasses any wrapper (the usual reason a theme's
  //    add-to-cart goes unnoticed).
  try {
    new PerformanceObserver(function (list) {
      list.getEntries().forEach(function (entry) {
        if (cartUrl.test(entry.name)) scheduleCheck();
      });
    }).observe({ type: "resource", buffered: false });
  } catch (e) {
    // Very old browser: fall back to the wrappers below.
  }

  // 2) Wrappers around fetch() and XMLHttpRequest, as a second net.
  var originalFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === "string" ? input : input && input.url;
    return originalFetch.apply(this, arguments).then(function (response) {
      if (cartUrl.test(url)) scheduleCheck();
      return response;
    });
  };

  var originalOpen = XMLHttpRequest.prototype.open;
  var originalSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__bogoCartCall = cartUrl.test(url);
    return originalOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__bogoCartCall) {
      this.addEventListener("loadend", scheduleCheck);
    }
    return originalSend.apply(this, arguments);
  };

  // 3) Page loads (including normal form-submit add-to-cart, which
  //    navigates) and back/forward cache restores.
  document.addEventListener("DOMContentLoaded", check);
  window.addEventListener("pageshow", check);
})();